/**
 * Training history — the IndexedDB {@link EffortStorePort}.
 *
 * History is **two** stores, and almost every rule in this file exists because
 * of that. Canonical effort sessions are what this build writes; legacy workout
 * records are what an earlier one wrote, adapted in and never written back
 * (F-HS-27). A session the user can see may live in either, so:
 *
 *  - a *mutation* requires **both** to be readable before either is touched
 *    (F-HS-09, F-HS-10) — deleting from the canonical store while the legacy
 *    store refuses would leave the legacy twin behind and the row would come
 *    back on the next refresh, looking to the user like a deletion that undid
 *    itself;
 *  - a *read* of one session consults the canonical store first, because native
 *    sessions win uuid collisions (F-HS-27);
 *  - **duplicate uuids close the store.** The identity that may not repeat is
 *    the id *inside the payload*, not the row key: a row filed under one key
 *    whose session claims another id is exactly the collision a key-only check
 *    cannot see, and two records claiming one uuid make "the session this row is"
 *    unanswerable. The app refuses rather than choosing, because choosing
 *    silently discards whichever it did not pick.
 *
 * **History is uncapped.** Nothing here takes a limit, imposes one, or prunes.
 * A record limit that silently drops the oldest sessions is indistinguishable,
 * to the person who trained them, from data loss.
 */

import type {
  ArchivedTermination,
  EffortSession,
  EffortSessionFields,
  EffortSessionId,
  ManualSaveResult,
  SchemaVersion,
  StoreReadResult,
  WriteOutcome,
} from "@ff/domain";
import {
  EFFORT_SESSION_SCHEMA_VERSION,
  LEGACY_WORKOUT_RECORD_SCHEMA_VERSION,
  STORE_IDS,
  rehydrateEffortSession,
  schemaVersion,
} from "@ff/domain";
import type {
  EffortArchiveOutcome,
  EffortClearOutcome,
  EffortDeletionOutcome,
  EffortHistoryGate,
  EffortStorePort,
  KeyedRecord,
  ObservedMutationGate,
  RecordStorePort,
} from "@ff/domain/ports";

import type { RecordDatabase } from "../kernel";
import { canonicalJson, createRecordStore } from "../kernel";
import type { LegacyEffortRow } from "./payload";
import {
  adaptLegacyRecord,
  decodeCanonicalEffortSession,
  decodeLegacyEffortRow,
  effortSessionIndex,
  legacyRecordIndex,
} from "./payload";

export const CANONICAL_SESSION_VERSION: SchemaVersion = schemaVersion(
  EFFORT_SESSION_SCHEMA_VERSION,
);
export const LEGACY_RECORD_VERSION: SchemaVersion = schemaVersion(
  LEGACY_WORKOUT_RECORD_SCHEMA_VERSION,
);

export function createCanonicalSessionStore(
  database: RecordDatabase,
): RecordStorePort<EffortSessionFields> {
  return createRecordStore<EffortSessionFields>({
    store: STORE_IDS.canonicalEffortSessions,
    schemaVersion: CANONICAL_SESSION_VERSION,
    database,
    decodePayload: decodeCanonicalEffortSession,
    index: effortSessionIndex,
    // See the file comment: the uuid that may not repeat is the one inside the
    // payload, because that is the one the rest of the app addresses a session by.
    identity: (record) => record.value.id,
  });
}

export function createLegacyRecordStore(
  database: RecordDatabase,
): RecordStorePort<LegacyEffortRow> {
  return createRecordStore<LegacyEffortRow>({
    store: STORE_IDS.legacyWorkoutRecords,
    schemaVersion: LEGACY_RECORD_VERSION,
    database,
    decodePayload: decodeLegacyEffortRow,
    index: legacyRecordIndex,
  });
}

/**
 * History's rules over any pair of record stores that hold its rows.
 *
 * Split from the factories for the same reason the plan library is: the
 * two-store transaction, the verified re-reads and the gate arithmetic have
 * nothing to do with where the bytes live, and the interesting failures — a
 * store that refuses, a write whose read-back disagrees — are only reachable by
 * substituting the records underneath.
 */
export function effortHistory(
  canonical: RecordStorePort<EffortSessionFields>,
  legacy: RecordStorePort<LegacyEffortRow>,
): EffortStorePort {
  return new RecordEffortStore(canonical, legacy);
}

export function createEffortStore(database: RecordDatabase): EffortStorePort {
  return effortHistory(
    createCanonicalSessionStore(database),
    createLegacyRecordStore(database),
  );
}

class RecordEffortStore implements EffortStorePort {
  readonly #canonical: RecordStorePort<EffortSessionFields>;
  readonly #legacy: RecordStorePort<LegacyEffortRow>;

  constructor(
    canonical: RecordStorePort<EffortSessionFields>,
    legacy: RecordStorePort<LegacyEffortRow>,
  ) {
    this.#canonical = canonical;
    this.#legacy = legacy;
  }

  async readSessions(): Promise<StoreReadResult<readonly EffortSessionFields[]>> {
    return mapLoaded(await this.#canonical.read(), (records) =>
      records.map((record) => record.value),
    );
  }

  async readLegacyRecords(): Promise<StoreReadResult<readonly EffortSessionFields[]>> {
    return mapLoaded(await this.#legacy.read(), (records) => {
      const adapted: EffortSessionFields[] = [];
      for (const record of records) {
        const session = adaptLegacyRecord(record.value);
        // Dropped, not refused (F-HS-27). The bytes stay untouched; one legacy
        // row this build cannot use must not close the whole of history.
        if (session !== undefined) adapted.push(session);
      }
      return adapted;
    });
  }

  async readSession(id: EffortSessionId): Promise<StoreReadResult<EffortSessionFields>> {
    const canonical = await this.#canonical.readRecord(id);
    // Native sessions win uuid collisions, so the canonical store answers first
    // and the legacy store is consulted only for ids it does not hold.
    if (canonical.status !== "missing") return canonical;

    const legacy = await this.#legacy.readRecord(id);
    if (legacy.status !== "loaded") return legacy;

    const adapted = adaptLegacyRecord(legacy.value);
    if (adapted === undefined) return { status: "missing", store: this.#legacy.store };
    return { ...legacy, value: adapted };
  }

  mutationGate(): EffortHistoryGate {
    return combineGates(this.#canonical.mutationGate(), this.#legacy.mutationGate());
  }

  async archive(
    session: EffortSession,
    termination: ArchivedTermination,
  ): Promise<EffortArchiveOutcome> {
    const existing = await this.#canonical.readRecord(session.id);
    if (existing.status === "unreadable") {
      return { kind: "unavailable", reason: existing.trigger };
    }
    if (existing.status === "loaded") {
      // Execution-entry ids are derived, so a retry produces the same record and
      // finding it already stored is the earlier attempt having succeeded — not
      // a conflict, and not a reason to write again.
      return { kind: "alreadyArchived", session: rehydrateEffortSession(existing.value) };
    }

    const outcome = await this.#canonical.commit([
      { kind: "put", key: session.id, value: session },
    ]);
    if (outcome.status === "refused") return { kind: "unavailable", reason: outcome.reason };
    if (outcome.status === "mismatch") {
      // The caller owes a retry with this exact termination, so the frozen
      // `endedAt` travels back out rather than being recomputed as "now"
      // (§4.5 rule 3): a session archived on the third attempt must not claim
      // the user trained until then.
      return { kind: "notConfirmed", termination };
    }
    return { kind: "archived", session };
  }

  async createManual(session: EffortSession): Promise<ManualSaveResult> {
    // F-EF-20's create preflight: **both** stores mutable before anything is
    // attempted. A uuid the legacy store already holds would otherwise be minted
    // a second time the moment the legacy store came back.
    const canonical = await this.#canonical.read();
    if (canonical.status === "unreadable") return { kind: "unavailable" };
    const legacy = await this.#legacy.read();
    if (legacy.status === "unreadable") return { kind: "unavailable" };

    // A legacy id is *reserved*, not in conflict. The legacy store is read-only,
    // so there is no current version for the editor to reopen against — which is
    // exactly what `conflict` would promise the user.
    if (holdsKey(legacy, session.id)) return { kind: "unavailable" };

    const existing = recordFor(canonical, session.id);
    if (existing !== undefined) return { kind: "conflict", current: existing };

    // F-EF-20 requires `revision === 1` and `createdAt === updatedAt` on create.
    // Both are audit-trail facts, so both report as `invalidAuditDates`.
    if (session.revision !== 1 || session.createdAt !== session.updatedAt) {
      return { kind: "invalid", error: { kind: "invalidAuditDates" } };
    }

    return this.#writeManual(session);
  }

  async updateManual(
    session: EffortSession,
    expectedRevision: number,
  ): Promise<ManualSaveResult> {
    const existing = await this.#canonical.readRecord(session.id);
    if (existing.status === "unreadable") return { kind: "unavailable" };
    if (existing.status === "missing") return { kind: "notFound" };

    const current = existing.value;
    // Every one of these is a conflict rather than a validation error: the
    // record on disk is not the record the editor was opened against, so the
    // user's edits are kept and the current version is handed back to reopen.
    if (current.source !== "manual") return { kind: "conflict", current };
    if (expectedRevision < 1) return { kind: "conflict", current };
    if (current.revision !== expectedRevision) return { kind: "conflict", current };
    // Checked arithmetic, not `>=`: an edit that skipped a revision was computed
    // against a record nobody read.
    if (session.revision !== expectedRevision + 1) return { kind: "conflict", current };
    if (session.createdAt !== current.createdAt) return { kind: "conflict", current };
    if (session.updatedAt < current.updatedAt) return { kind: "conflict", current };
    if (
      session.provenance.deviceAuthority !== current.provenance.deviceAuthority ||
      session.provenance.externalSourceName !== current.provenance.externalSourceName
    ) {
      return { kind: "conflict", current };
    }

    return this.#writeManual(session);
  }

  async deleteSession(id: EffortSessionId): Promise<EffortDeletionOutcome> {
    const canonical = await this.#canonical.read();
    if (canonical.status === "unreadable") {
      return { kind: "unavailable", reason: canonical.trigger };
    }
    const legacy = await this.#legacy.read();
    if (legacy.status === "unreadable") {
      return { kind: "unavailable", reason: legacy.trigger };
    }

    const inCanonical = holdsKey(canonical, id);
    const inLegacy = holdsKey(legacy, id);
    if (!inCanonical && !inLegacy) return { kind: "notFound" };

    if (inCanonical) {
      const outcome = await this.#canonical.commit([{ kind: "remove", key: id }]);
      if (outcome.status !== "verified") return failedDeletion(outcome);
    }
    if (inLegacy) {
      const outcome = await this.#legacy.commit([{ kind: "remove", key: id }]);
      if (outcome.status !== "verified") return failedDeletion(outcome);
    }

    // Refresh and re-verify absence from **both** (F-HS-09). The caller cascades
    // the reflection only once this says `deleted`, so a failed history delete
    // can never orphan a note away from the session it describes (F-HS-26).
    const afterCanonical = await this.#canonical.readRecord(id);
    const afterLegacy = await this.#legacy.readRecord(id);
    if (afterCanonical.status !== "missing" || afterLegacy.status !== "missing") {
      return { kind: "notConfirmed" };
    }
    return { kind: "deleted" };
  }

  async clear(): Promise<EffortClearOutcome> {
    const canonical = await this.#canonical.read();
    if (canonical.status === "unreadable") {
      return { kind: "unavailable", reason: canonical.trigger };
    }
    const legacy = await this.#legacy.read();
    if (legacy.status === "unreadable") {
      return { kind: "unavailable", reason: legacy.trigger };
    }

    // Effort sessions first, then legacy records, then re-verify emptiness
    // (F-HS-10). Saved plans are deliberately untouched: they are authored
    // material, not a training record.
    const canonicalOutcome = await this.#canonical.commit(removalsFor(canonical));
    if (canonicalOutcome.status !== "verified") {
      return { kind: "notConfirmed", remainingSessions: await this.#remaining() };
    }
    const legacyOutcome = await this.#legacy.commit(removalsFor(legacy));
    if (legacyOutcome.status !== "verified") {
      return { kind: "notConfirmed", remainingSessions: await this.#remaining() };
    }

    const remaining = await this.#remaining();
    // The user-facing promise on failure is *"Sessions that remain are still
    // protected"* — a claim the app may only make if it knows some remained.
    if (remaining > 0) return { kind: "notConfirmed", remainingSessions: remaining };
    return { kind: "cleared" };
  }

  /* --- internals --------------------------------------------------------- */

  async #writeManual(session: EffortSession): Promise<ManualSaveResult> {
    const outcome = await this.#canonical.commit([
      { kind: "put", key: session.id, value: session },
    ]);
    // The distinction F-EF-18 insists on: `unavailable` means the store refused
    // before anything was attempted, `saveNotConfirmed` means bytes were written
    // and the app cannot prove what they say. Different copy, different retry.
    if (outcome.status === "refused") return { kind: "unavailable" };
    if (outcome.status === "mismatch") return { kind: "saveNotConfirmed" };

    // F-EF-21: the comparison is repeated after the read model refreshes. The
    // kernel already verified the row inside its transaction; this asks the
    // narrower question the user cares about — is the session in history now the
    // session they saved — and returns the re-read value rather than the input.
    const stored = await this.#canonical.readRecord(session.id);
    if (stored.status !== "loaded") return { kind: "saveNotConfirmed" };
    if (canonicalJson(stored.value) !== canonicalJson(session)) {
      return { kind: "saveNotConfirmed" };
    }
    return { kind: "saved", session: rehydrateEffortSession(stored.value) };
  }

  /**
   * How many sessions survived. A store that has become unreadable contributes
   * nothing, because its rows can no longer be counted — and over-reporting here
   * would tell the user records remain that may already be gone.
   */
  async #remaining(): Promise<number> {
    const canonical = await this.#canonical.read();
    const legacy = await this.#legacy.read();
    return countOf(canonical) + countOf(legacy);
  }
}

/* --------------------------------------------------------------------------- *
 * Shared helpers
 * --------------------------------------------------------------------------- */

/**
 * Maps the value inside a loaded read without collapsing the union. Every
 * `T | undefined` shortcut is how "unreadable" degrades into "empty", so the
 * three cases stay three cases all the way to the caller.
 */
function mapLoaded<A, B>(
  result: StoreReadResult<A>,
  transform: (value: A) => B,
): StoreReadResult<B> {
  if (result.status !== "loaded") return result;
  return {
    status: "loaded",
    store: result.store,
    schemaVersion: result.schemaVersion,
    value: transform(result.value),
    quarantined: result.quarantined,
  };
}

function holdsKey<T>(result: StoreReadResult<readonly KeyedRecord<T>[]>, key: string): boolean {
  if (result.status !== "loaded") return false;
  return result.value.some((record) => record.key === key);
}

function recordFor<T>(
  result: StoreReadResult<readonly KeyedRecord<T>[]>,
  key: string,
): T | undefined {
  if (result.status !== "loaded") return undefined;
  return result.value.find((record) => record.key === key)?.value;
}

function removalsFor<T>(
  result: StoreReadResult<readonly KeyedRecord<T>[]>,
): readonly { readonly kind: "remove"; readonly key: string }[] {
  if (result.status !== "loaded") return [];
  return result.value.map((record) => ({ kind: "remove", key: record.key }) as const);
}

function countOf<T>(result: StoreReadResult<readonly KeyedRecord<T>[]>): number {
  return result.status === "loaded" ? result.value.length : 0;
}

function failedDeletion(outcome: WriteOutcome): EffortDeletionOutcome {
  if (outcome.status === "refused") return { kind: "unavailable", reason: outcome.reason };
  return { kind: "notConfirmed" };
}

/**
 * Both stores as one gate.
 *
 * `unknown` wins over `allowed`: before a store has been read nothing has
 * established that it is readable, and answering `allowed` there is how a Delete
 * button appears over a store that will refuse it — the user's action then fails
 * at the last possible moment instead of the first.
 */
function combineGates(
  canonical: ObservedMutationGate,
  legacy: ObservedMutationGate,
): EffortHistoryGate {
  const observed = [canonical, legacy];
  for (const gate of observed) {
    if (gate.status === "unknown") return { status: "unknown" };
  }
  for (const gate of observed) {
    if (gate.status !== "known" || gate.gate.allowed) continue;
    // Naming the store that refused is what lets the message be specific about
    // which half of history is damaged.
    return { status: "blocked", store: gate.store, reason: gate.gate.reason };
  }
  return { status: "allowed" };
}
